import type {CollectionField} from '@packages/systems/dynamo/types';

type EcommExtraFields = {
  category: CollectionField[];
  product: CollectionField[];
  sku: CollectionField[];
};

// In addition to the "default" fields, these are other fields that the user gets for "free":
// Note: These get baked into site data when a store is set up, so they will not change on an
// existing site without a migration. New not-required fields will get added via `upgradeEcommCollections`.
// ---------------------------------------------------------------------------------------------------
// Adding a new field may require published site snapshots in Cypress tests to be updated, refer to
// `docs/tools/cypress/published-site-tests.md` for steps.
// ---------------------------------------------------------------------------------------------------
// In order for new fields to show up for a certain product type on the product or variant form, the field will
// need to be added to a specific template type or a default (`DEFAULT_PRODUCT_TYPE_PRODUCT_FIELDS` or `DEFAULT_PRODUCT_TYPE_SKU_FIELDS`).
// See `docs/app/ecommerce/product-types.md/#ii-template-product-types` for more info.
// ---------------------------------------------------------------------------------------------------
export const ecommExtraFields: EcommExtraFields = {
  category: [],
  product: [
    {
      name: 'SKU Properties',
      slug: 'sku-properties',
      type: 'CommercePropTable',
      required: false,
      editable: true,
    },
    {
      name: 'Categories',
      slug: 'category',
      type: 'ItemRefSet',
      helpText: 'Add this product to one or more categories.',
      required: false,
      editable: true,
      validations: {
        collectionId: undefined, // Will be populated with correct value at creation.
      },
    },
    {
      name: 'Description',
      slug: 'description',
      type: 'PlainText',
      required: false,
      editable: true,
    },
    {
      name: 'Requires Shipping',
      slug: 'shippable',
      type: 'Bool',
      required: false,
      editable: true,
    },
    {
      name: 'Tax Category',
      slug: 'tax-category',
      type: 'EnumOption',
      required: false,
      editable: true,
      validations: {
        enumName: 'TaxCategories',
      },
    },
    {
      name: 'Default SKU',
      slug: 'default-sku',
      type: 'ItemRef',
      required: false, // False, so we can create the SKU, and then update.
      editable: true,
      validations: {
        collectionId: undefined, // Will be populated with correct value at creation.
      },
    },
    {
      name: 'Product type',
      slug: 'ec-product-type',
      type: 'Option',
      required: false,
      editable: true,
      validations: {
        options: [
          {id: 'ff42fee0113744f693a764e3431a9cc2', name: 'Physical'},
          {id: 'f22027db68002190aef89a4a2b7ac8a1', name: 'Digital'},
          {id: 'c599e43b1a1c34d5a323aedf75d3adf6', name: 'Service'},
          {id: 'e348fd487d0102946c9179d2a94bb613', name: 'Membership'},
          {id: 'b6ccc1830db4b1babeb06a9ac5f6dd76', name: 'Advanced'},
        ],
      },
    },
  ],
  sku: [
    {
      name: 'SKU Values',
      slug: 'sku-values',
      type: 'CommercePropValues',
      required: true,
      editable: true,
    },
    {
      name: 'Product',
      slug: 'product',
      type: 'ItemRef',
      required: true,
      editable: true,
      validations: {
        collectionId: undefined, // Will be populated with correct value at creation.
      },
    },
    {
      name: 'Main Image',
      slug: 'main-image',
      type: 'ImageRef',
      required: false,
      editable: true,
      validations: {
        acceptedExtensions: ['jpeg', 'jpg', 'gif', 'png', 'webp', 'avif'], // To prevent upload svg as product image, as it is not render properly in Gmail web client
      },
    },
    {
      name: 'More Images',
      slug: 'more-images',
      type: 'Set',
      innerType: 'ImageRef',
      required: false,
      editable: true,
      validations: {
        acceptedExtensions: ['jpeg', 'jpg', 'gif', 'png', 'webp', 'avif'], // To prevent upload svg as product image, as it is not render properly in Gmail web client
      },
    },
    {
      name: 'Price',
      slug: 'price',
      type: 'CommercePrice',
      required: true,
      editable: true,
    },
    {
      name: 'Compare-at price',
      slug: 'compare-at-price',
      type: 'CommercePrice',
      required: false,
      editable: true,
    },
    {
      name: 'Downloads',
      slug: 'download-files',
      type: 'Set',
      innerType: 'ExtFileRef',
      required: false,
      editable: true,
    },
    {
      name: 'Subscription Plan',
      slug: 'ec-sku-subscription-plan',
      type: 'SubscriptionPlan',
      required: false,
      editable: true,
    },
    {
      name: 'Width',
      slug: 'width',
      type: 'Number',
      required: false,
      editable: true,
      validations: {
        minValue: 0,
      },
    },
    {
      name: 'Length',
      slug: 'length',
      type: 'Number',
      required: false,
      editable: true,
      validations: {
        minValue: 0,
      },
    },
    {
      name: 'Height',
      slug: 'height',
      type: 'Number',
      required: false,
      editable: true,
      validations: {
        minValue: 0,
      },
    },
    {
      name: 'Weight',
      slug: 'weight',
      type: 'Number',
      required: false,
      editable: true,
      validations: {
        minValue: 0,
      },
    },
    {
      name: 'SKU',
      slug: 'sku',
      type: 'PlainText',
      required: false,
      editable: true,
      validations: {
        singleLine: true,
      },
    },
    {
      name: 'Billing method',
      slug: 'ec-sku-billing-method',
      type: 'EnumOption',
      required: false,
      editable: true,
      validations: {
        enumName: 'BillingMethods',
      },
    },
  ],
};
